import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

export interface OrderItem {
  id: string;
  order_id: string;
  producer_id: string;
  product_id: string | null;
  item_type: string;
  title: string;
  sku: string | null;
  quantity: number;
  unit_amount: number;
  total_amount: number;
  metadata: {
    image_url?: string;
    [key: string]: unknown;
  };
  created_at: string;
}

export interface OrderDetails {
  id: string;
  producer_id: string;
  user_id: string | null;
  status: string;
  currency: string;
  customer_email: string;
  customer_name: string | null;
  customer_phone: string | null;
  delivery_type: string;
  shipping_fee_amount: number;
  subtotal_amount: number;
  total_amount: number;
  external_order_id: string | null;
  payment_status: string;
  paid_at: string | null;
  paid_method: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProducerInfo {
  id: string;
  name: string;
  slug: string | null;
  email: string | null;
  phone: string | null;
}

export function useOrderDetails(orderId: string | undefined) {
  const { user } = useAuth();

  // Fetch order
  const orderQuery = useQuery({
    queryKey: ['customer-order', orderId, user?.id],
    queryFn: async () => {
      if (!orderId || !user) return null;

      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('id', orderId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data as OrderDetails | null;
    },
    enabled: !!orderId && !!user,
  });

  // Fetch order items
  const itemsQuery = useQuery({
    queryKey: ['customer-order-items', orderId, user?.id],
    queryFn: async () => {
      if (!orderId || !user) return [];

      const { data, error } = await supabase
        .from('order_items')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      // Parse metadata for each item
      return (data || []).map((item): OrderItem => ({
        ...item,
        metadata: typeof item.metadata === 'object' ? (item.metadata as OrderItem['metadata']) : {},
      }));
    },
    enabled: !!orderId && !!user,
  });

  // Fetch producer info
  const producerQuery = useQuery({
    queryKey: ['customer-order-producer', orderQuery.data?.producer_id],
    queryFn: async () => {
      if (!orderQuery.data?.producer_id) return null;

      const { data, error } = await supabase
        .from('producers')
        .select('id, name, slug, email, phone')
        .eq('id', orderQuery.data.producer_id)
        .maybeSingle();

      if (error) throw error;
      return data as ProducerInfo | null;
    },
    enabled: !!orderQuery.data?.producer_id,
  });

  return {
    order: orderQuery.data,
    items: itemsQuery.data || [],
    producer: producerQuery.data,
    isLoading: orderQuery.isLoading || itemsQuery.isLoading,
    isLoadingProducer: producerQuery.isLoading,
    error: orderQuery.error || itemsQuery.error || producerQuery.error,
  };
}
